/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Business Rules - Business Domain Constants
 *
 * Core business rules, calculation parameters, and operational limits.
 * Used for:
 * - Cost calculations and pricing
 * - Approval workflows
 * - Data validation
 * - Business logic enforcement
 */

// ============================================================================
// DEFAULT RATES - AS DECIMALS FOR CALCULATIONS
// ============================================================================

export const BUSINESS_RULES = {
  // Default Rates (as decimals for calculations)
  DEFAULT_OVERHEAD_RATE: 0.15, // 15%
  DEFAULT_MARGIN_RATE: 0.2, // 20% margin rate fallback (as decimal)
  DEFAULT_PROFIT_RATE: 0.1, // 10%
  DEFAULT_TAX_RATE: 0.085, // 8.5%
  DEFAULT_LABOR_BURDEN_RATE: 0.25, // 25%
  DEFAULT_CONTINGENCY_RATE: 0.05, // 5%

  // Safety caps (as decimals). Values above these are almost always unit-mistakes
  // (e.g. a user typing "0.8" meaning 0.8% being silently read as 80%). The
  // resolver clamps at these caps and logs so the bug surfaces.
  MAX_SANE_PROFIT_MARGIN: 0.5, // 50% net margin is already well beyond industry norms
  MAX_SANE_OVERHEAD_RATE: 0.5, // 50% overhead on direct costs
  // Labor-hour absorption ceiling: dollars of overhead loaded onto each billable
  // labor hour. Numbers above this ($150/hr) almost always mean someone entered
  // annual overhead or monthly overhead in the wrong field. Landscape/trade
  // crews in 2026 are typically $30-$80/hr fully burdened; absorption adds on
  // top of that, so $150 covers extreme-ceiling legitimate cases.
  MAX_SANE_OVERHEAD_PER_LABOR_HOUR: 150,

  // Expiration Times (in days)
  ESTIMATE_EXPIRATION_DAYS: 30,
  PROPOSAL_EXPIRATION_DAYS: 60,
  QUOTE_EXPIRATION_DAYS: 15,

  // Amount Limits
  MIN_ESTIMATE_AMOUNT: 50.0,
  MAX_ESTIMATE_AMOUNT: 1000000.0,
  MIN_PROPOSAL_AMOUNT: 100.0,
  MAX_PROPOSAL_AMOUNT: 5000000.0,

  // Approval Thresholds
  ESTIMATE_APPROVAL_THRESHOLD: 10000.0,
  PROPOSAL_APPROVAL_THRESHOLD: 50000.0,
  CONTRACT_APPROVAL_THRESHOLD: 100000.0,

  // Retention Periods (in days)
  PROJECT_RETENTION_DAYS: 2555, // 7 years
  DOCUMENT_RETENTION_DAYS: 2555, // 7 years
  COMMUNICATION_RETENTION_DAYS: 1825, // 5 years

  // Notification Delays (in hours)
  ESTIMATE_EXPIRATION_WARNING_HOURS: 72,
  PROPOSAL_EXPIRATION_WARNING_HOURS: 168, // 1 week
  FOLLOW_UP_REMINDER_HOURS: 48,
} as const;

// ============================================================================
// INDUSTRY-SPECIFIC CONSTANTS
// ============================================================================

import { INDUSTRIES } from "./industries";

export const INDUSTRY_CONSTANTS = {
  [INDUSTRIES.LANDSCAPING]: {
    DEFAULT_UNITS: ["sq_ft", "linear_ft", "each", "hour"],
    COMMON_MATERIALS: ["soil", "mulch", "plants", "pavers", "irrigation"],
    // Note: Labor rates should come from user's cost database, not hardcoded constants
  },
  [INDUSTRIES.PLUMBING]: {
    DEFAULT_UNITS: ["linear_ft", "each", "hour", "fixture"],
    COMMON_MATERIALS: ["pipe", "fittings", "fixtures", "valves", "pumps"],
  },
  [INDUSTRIES.ELECTRICAL]: {
    DEFAULT_UNITS: ["linear_ft", "each", "hour", "circuit"],
    COMMON_MATERIALS: ["wire", "conduit", "outlets", "switches", "panels"],
  },
  [INDUSTRIES.HVAC]: {
    DEFAULT_UNITS: ["ton", "each", "hour", "sq_ft"],
    COMMON_MATERIALS: ["units", "ductwork", "thermostats", "refrigerant"],
  },
  [INDUSTRIES.CONSTRUCTION]: {
    DEFAULT_UNITS: ["sq_ft", "linear_ft", "cubic_ft", "hour"],
    COMMON_MATERIALS: ["lumber", "concrete", "steel", "drywall", "insulation"],
  },
} as const;

// ============================================================================
// CALCULATION UTILITIES
// ============================================================================

/**
 * Calculates overhead amount based on subtotal
 */
export const calculateOverhead = (
  subtotal: number,
  rate: number = BUSINESS_RULES.DEFAULT_OVERHEAD_RATE,
): number => subtotal * rate;

/**
 * Calculates tax amount based on total
 */
export const calculateTax = (
  total: number,
  rate: number = BUSINESS_RULES.DEFAULT_TAX_RATE,
): number => total * rate;

/**
 * Validates if an amount is within acceptable limits
 */
export const isValidEstimateAmount = (amount: number): boolean =>
  amount >= BUSINESS_RULES.MIN_ESTIMATE_AMOUNT &&
  amount <= BUSINESS_RULES.MAX_ESTIMATE_AMOUNT;

/**
 * Validates if an amount is within acceptable limits
 */
export const isValidProposalAmount = (amount: number): boolean =>
  amount >= BUSINESS_RULES.MIN_PROPOSAL_AMOUNT &&
  amount <= BUSINESS_RULES.MAX_PROPOSAL_AMOUNT;

/**
 * Checks if an estimate requires approval
 */
export const requiresEstimateApproval = (amount: number): boolean =>
  amount >= BUSINESS_RULES.ESTIMATE_APPROVAL_THRESHOLD;

/**
 * Checks if a proposal requires approval
 */
export const requiresProposalApproval = (amount: number): boolean =>
  amount >= BUSINESS_RULES.PROPOSAL_APPROVAL_THRESHOLD;
